// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cn from 'classnames/bind';
// Hooks
import { useDispatch } from 'react-redux';
import { useRebookOrder } from 'src/containers/Account/OrderPage/OrderPage.hooks';
// Utils
import { getStatusInfo } from 'src/utils/status';
import { formatTimeForOrdersListDisplay, scheduledForFormat } from 'src/utils/date';
import { LABEL } from 'src/utils/status/order/constants';
import { push, accountOrderPath } from 'src/utils/paths';
import { useCartServiceSchedulingForCompletedOrders } from 'src/utils/serviceScheduling/useCartServiceSchedulingForCompletedOrders';
// Components & Styles
import OrderStatusHeader from 'src/containers/Account/OrderPage/Parts/OrderStatusHeader';
import { PanelV2 } from 'src/components/HTKit/Elements/PanelV2';
import { RecurrencePattern } from './OrderCard.RecurrencePattern';
import styles from './styles.scss';
// Local constants
import BADGE_TYPE from './constants';

/** @param {{order: Order, label: string}} props */
const AppointmentStatusDisplay = ({ order, label }) => {
  const apptStatusStyles = cn('p1', styles.dateTime);
  const { timezone = '' } = order;

  const { isExactTimeSchedule, isTimeWindowSchedule } = useCartServiceSchedulingForCompletedOrders(
    order,
  );

  const isTimeWindowOrExactTimeSchedule = isTimeWindowSchedule() || isExactTimeSchedule();

  const showUnconfirmedText =
    ![LABEL.CANCELLED, LABEL.UNSERVICEABLE, LABEL.COMPLETED].includes(label) &&
    !order.appointmentStart &&
    !order.remote;
  const showScheduledTime = order.appointmentStart && !order.completedAt;
  const showCompletedTime = order.completedAt && label !== LABEL.UNSERVICEABLE;
  const showRemoteText = order.remote && !order.completedAt;

  const unconfirmedAppointmentText = isTimeWindowOrExactTimeSchedule
    ? 'Appointment confirmed'
    : 'Appointment to be confirmed';

  return (
    <p className={apptStatusStyles}>
      {showScheduledTime &&
        formatTimeForOrdersListDisplay({ date: order.appointmentStartDateTime, timezone })}
      {!order.appointmentStart && LABEL.CANCELLED && ''}
      {showCompletedTime && scheduledForFormat(order.completedAtDateTime, { timezone })}
      {showUnconfirmedText && unconfirmedAppointmentText}
      {showRemoteText && 'Available from 7:00am to 9:00pm'}
    </p>
  );
};

/** @param {{order: Order, isPastOrder?: boolean}} props */
export const OrderCard = ({ order, isPastOrder }) => {
  const dispatch = useDispatch();

  const { label, panel } = getStatusInfo({ order });
  const badgeType = get(BADGE_TYPE, `${panel}['badge']`, {});

  // ##########
  // RECURRING
  // ##########
  const isOrderRecurring = order.recurring && order.recurringService;
  const showRecurrencePattern = isOrderRecurring && !isPastOrder;

  // ##########
  // REBOOKING
  // ##########
  const { canRebookOrder, rebookOrder } = useRebookOrder({
    order,
  });
  const showRebookingLink = canRebookOrder(order);

  const handleRebookingClick = (e) => {
    if (e) e.stopPropagation();
    rebookOrder(order);
  };

  return (
    <PanelV2 lightShadow className={styles.panel}>
      <div
        role="button"
        aria-pressed="false"
        onClick={() => dispatch(push(accountOrderPath(order.id)))}
        className={styles.cardTitleWrapper}
      >
        <OrderStatusHeader orderId={order.id} order={order} badgeType={badgeType} label={label} />
        <AppointmentStatusDisplay order={order} label={label} />

        {showRecurrencePattern && <RecurrencePattern order={order} className="marginTop-tiny1" />}
        {showRebookingLink && (
          <button
            className="plainButton paddingTop-small teal text-weight-med cursorPointer"
            onClick={handleRebookingClick}
          >
            Rebook
          </button>
        )}
      </div>
    </PanelV2>
  );
};

OrderCard.propTypes = {
  order: PropTypes.object.isRequired,
  isPastOrder: PropTypes.bool,
};

AppointmentStatusDisplay.propTypes = {
  order: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
};

/** @typedef {import('src/types/order').Order} Order */
